import { useReducer, FC, useEffect, useState } from 'react'
import { EmployeeFilterModal, CurrencyAndCountry } from '../../employees/FilterModal/EmployeeFilterModal'
import { useAuth } from '../../../context/auth-context'
import { CompanySubordinate, EmployeeLevelEnum, EmployeesWithWrongStatusOrMissingContractDto, ResignedEmployeeDto } from '../../../types'
import {
  emptyFormData,
  filterTableData,
  EmployeeInfoBoxFilter,
  employeeTabInitialFilter,
  filterCompanySettingsEmployeesFromInfoBoxFilters,
  EmployeeModalFormDataType,
  getOldestSyncUTCDate,
} from '../../employees/util'
import { CompanySettingsEmployeeTable } from './EmployeesTable'
import { EmployeeInfoBox } from '../../employees/EmployeeInfoBox'
import { ResignedEmployeesInfoBox } from '../../employees/ResignedEmployees/ResignedEmployeesInfoBox'
import { ResignedEmployeesList } from '../../employees/ResignedEmployees/ResignedEmployeesList'
import { useTranslation } from 'react-i18next'

export type CompanySettingsEmployeeTableData = Pick<
  CompanySubordinate,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'currentLevel'
  | 'avatarUrl'
  | 'buName'
  | 'currencyForPayments'
  | 'replyEntryDate'
  | 'contractValidTo'
  | 'country'
  | 'salary'
  | 'targetBonus'
  | 'resultScore'
  | 'careerPathEvaluation'
  | 'careerPath'
  | 'timezone'
  | 'skillStatus'
>

type ReducerTypes = 'initFilter' | 'updateModalFilter' | 'updateInfoBoxFilter' | 'resetFilter' | 'setModalOpen'
type EmployeesViewState = {
  filteredData?: CompanySettingsEmployeeTableData[]
  filterResultLength?: number
  modalFilterActive?: boolean
  modalFormValues?: EmployeeModalFormDataType
  modalOpen?: boolean
}

type EmployeesViewProps = {
  initialActiveEmployeesData: CompanySettingsEmployeeTableData[]
  resignedEmployeesData: ResignedEmployeeDto[]
  employeeLevels: EmployeeLevelEnum[]
  companyId: string
  employeesWithWrongStatusOrMissingContract: EmployeesWithWrongStatusOrMissingContractDto[]
}

export const EmployeesView: FC<EmployeesViewProps> = ({
  initialActiveEmployeesData,
  resignedEmployeesData,
  employeesWithWrongStatusOrMissingContract,
  employeeLevels,
  companyId,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const [resignedActive, setResignedActive] = useState<boolean>(false)
  const [infoBoxFilter, setInfoBoxFilter] = useState<EmployeeInfoBoxFilter>(employeeTabInitialFilter)

  const initialState: EmployeesViewState = {
    filteredData: initialActiveEmployeesData,
    filterResultLength: undefined,
    modalFilterActive: false,
    modalFormValues: emptyFormData,
    modalOpen: false,
  }

  useEffect(() => {
    dispatch({ type: 'initFilter', value: {} })
  }, [initialActiveEmployeesData])

  useEffect(() => {
    dispatch({
      type: 'updateInfoBoxFilter',
      value: {},
    })
  }, [infoBoxFilter])

  const reducer = (actState: EmployeesViewState, action: { type: ReducerTypes; value: EmployeesViewState }) => {
    const nextState = { ...actState }
    switch (action.type) {
      case 'updateModalFilter':
        nextState.filteredData = action.value.filteredData
        nextState.filterResultLength = action.value.filterResultLength
        nextState.modalFilterActive = action.value.modalFilterActive
        nextState.modalOpen = false
        nextState.modalFormValues = action.value.modalFormValues
        break
      case 'updateInfoBoxFilter':
        const filteredDataFromModalFilters: CompanySettingsEmployeeTableData[] = !!actState.modalFilterActive
          ? filterTableData(initialActiveEmployeesData, actState.modalFormValues)
          : initialActiveEmployeesData
        const filteredData: CompanySettingsEmployeeTableData[] = filterCompanySettingsEmployeesFromInfoBoxFilters(
          filteredDataFromModalFilters,
          infoBoxFilter,
        )
        nextState.filteredData = filteredData
        nextState.filterResultLength = action.value.filterResultLength
        break
      case 'setModalOpen':
        nextState.modalOpen = action.value.modalOpen
        break
      case 'initFilter':
      case 'resetFilter':
        setInfoBoxFilter(employeeTabInitialFilter)
        return { ...initialState }
      default:
        throw new Error()
    }
    return nextState
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const hasResultScore = initialActiveEmployeesData.map((elem) => elem.resultScore).filter((resultScore) => resultScore).length > 0
  const hasCareerPathData = initialActiveEmployeesData.filter((elem) => elem.careerPathEvaluation).length > 0
  const possibleLevels: EmployeeLevelEnum[] = [...new Set(initialActiveEmployeesData.map((e) => e.currentLevel as EmployeeLevelEnum))]
  const possibleBUs: string[] = [...new Set(initialActiveEmployeesData.map((e) => e.buName))]
  const atLeastOneExpiredOrUnknownSkillStatus: boolean = initialActiveEmployeesData.some(
    (sub) => !sub.skillStatus || sub.skillStatus.expired,
  )
  const oldestSyncUTCDate: string | undefined = getOldestSyncUTCDate(initialActiveEmployeesData)

  const onSubmit = (modalValues: EmployeeModalFormDataType) => {
    const filteredDataFromModalFilters: CompanySettingsEmployeeTableData[] = filterTableData(initialActiveEmployeesData, modalValues)
    const filteredData: CompanySettingsEmployeeTableData[] = filterCompanySettingsEmployeesFromInfoBoxFilters(
      filteredDataFromModalFilters,
      infoBoxFilter,
    )
    dispatch({
      type: 'updateModalFilter',
      value: {
        filteredData: filteredData,
        filterResultLength: filteredData.length,
        modalFormValues: modalValues,
        modalFilterActive: JSON.stringify(modalValues) !== JSON.stringify(emptyFormData),
      },
    })
  }
  const onCancel = () => {
    dispatch({ type: 'setModalOpen', value: { modalOpen: false } })
  }
  const resetTableData = () => {
    dispatch({
      type: 'resetFilter',
      value: { modalFilterActive: false },
    })
  }
  const switchContentButtons = [
    { text: t('global.employeeCompanyRelation.active'), onClick: () => setResignedActive(false) },
    { text: t('global.employeeCompanyRelation.resigned'), onClick: () => setResignedActive(true) },
  ]
  const mapEmployeesToCurrencyAndCountry = (employees: CompanySettingsEmployeeTableData[]): CurrencyAndCountry[] => {
    const modalData: CurrencyAndCountry[] = []

    employees.forEach((employee: CompanySettingsEmployeeTableData) => {
      if (employee.currencyForPayments && employee.currentLevel) {
        modalData.push({
          currency: employee.currencyForPayments,
          country: employee.country,
        })
      }
    })
    return modalData
  }

  return (
    <>
      <EmployeeFilterModal
        filterModalData={mapEmployeesToCurrencyAndCountry(initialActiveEmployeesData)}
        modalFormValues={state.modalFormValues ? state.modalFormValues : emptyFormData}
        hasResultScore={hasResultScore}
        onSubmit={onSubmit}
        onCancel={onCancel}
        modalOpen={!!state.modalOpen}
        user={user}
      />
      {!resignedActive ? (
        <>
          <EmployeeInfoBox
            hasCareerPathData={hasCareerPathData}
            resetTableData={resetTableData}
            filterResultLength={state.filteredData !== undefined ? state.filteredData.length : 0}
            setModalOpen={(value: boolean) => dispatch({ type: 'setModalOpen', value: { modalOpen: value } })}
            totalOfEmployees={initialActiveEmployeesData.length}
            swichContentButtons={switchContentButtons}
            infoBoxFilter={infoBoxFilter}
            setInfoBoxFilter={setInfoBoxFilter}
            modalFilterActive={!!state.modalFilterActive}
            possibleLevels={possibleLevels}
            possibleBUs={possibleBUs}
            employeeLevels={employeeLevels}
            atLeastOneExpiredOrUnknownSkillStatus={atLeastOneExpiredOrUnknownSkillStatus}
            companyId={companyId}
            isCompanySettings={true}
            oldestSyncUTCDate={oldestSyncUTCDate}
          />
          <CompanySettingsEmployeeTable
            tableData={state.filteredData ? state.filteredData : []}
            filterResultLength={state.filterResultLength}
            user={user}
            employeeLevels={employeeLevels}
          />
        </>
      ) : (
        <>
          <ResignedEmployeesInfoBox
            totalOfEmployees={resignedEmployeesData.length}
            employeesWithWrongStatusOrMissingContract={employeesWithWrongStatusOrMissingContract}
            swichContentButtons={switchContentButtons}
          />
          <ResignedEmployeesList listData={resignedEmployeesData} employeeLevels={employeeLevels} />
        </>
      )}
    </>
  )
}
